import { FRAME_RENDER_WIDTH } from 'constants/studioCanvas';

export type TPreviewDeviceKey =
  'ios-small' | 'ios-large' | 'android-small' | 'android-medium' | 'android-large';

export type TPreviewDeviceOs = 'iOS' | 'Android';

type TPreviewDevice = {
  key: TPreviewDeviceKey;
  os: TPreviewDeviceOs;
  label: string;
  ratioLabel: string;
  ratioH: number;
};

export const PREVIEW_DEVICES: TPreviewDevice[] = [
  { key: 'ios-small', os: 'iOS', label: 'Small Device', ratioLabel: '9:16', ratioH: 16 },
  { key: 'ios-large', os: 'iOS', label: 'Large Device', ratioLabel: '9:19,5', ratioH: 19.5 },
  { key: 'android-small', os: 'Android', label: 'Small Device', ratioLabel: '9:16', ratioH: 16 },
  { key: 'android-medium', os: 'Android', label: 'Medium Device', ratioLabel: '9:18', ratioH: 18 },
  { key: 'android-large', os: 'Android', label: 'Large Device', ratioLabel: '9:20', ratioH: 20 },
];

export const DEFAULT_PREVIEW_DEVICE: TPreviewDeviceKey = 'android-large';

export const CANVAS_CHROME_HEIGHT = 190;

export type TFrameMetrics = {
  width: number;
  height: number;
  scale: number;
  screenHeight: number;
};

const CONTENT_RATIO_H = 20;

const DEVICE_BY_KEY: Record<TPreviewDeviceKey, TPreviewDevice> = PREVIEW_DEVICES.reduce(
  (acc, device) => ({ ...acc, [device.key]: device }),
  {} as Record<TPreviewDeviceKey, TPreviewDevice>
);

const getPreviewDevice = (key: TPreviewDeviceKey): TPreviewDevice => DEVICE_BY_KEY[key];

export const getFrameMetrics = (
  key: TPreviewDeviceKey,
  availableHeight: number,
  fitToContent = false
): TFrameMetrics => {
  const logicalWidth = FRAME_RENDER_WIDTH;
  const contentLogicalHeight = (logicalWidth * CONTENT_RATIO_H) / 9;
  const screenLogicalHeight = (logicalWidth * getPreviewDevice(key).ratioH) / 9;
  const fitHeight = fitToContent ? contentLogicalHeight : screenLogicalHeight;
  const scale = availableHeight > 0 ? Math.min(1, availableHeight / fitHeight) : 1;
  return {
    width: Math.round(logicalWidth * scale),
    height: Math.round(contentLogicalHeight * scale),
    scale,
    screenHeight: Math.round(screenLogicalHeight * scale),
  };
};

export const getContentFrameMetrics = (availableHeight: number): TFrameMetrics => {
  const logicalWidth = FRAME_RENDER_WIDTH;
  const contentLogicalHeight = (logicalWidth * CONTENT_RATIO_H) / 9;
  const scale = availableHeight > 0 ? Math.min(1, availableHeight / contentLogicalHeight) : 1;
  const size = {
    width: Math.round(logicalWidth * scale),
    height: Math.round(contentLogicalHeight * scale),
  };
  return { ...size, scale, screenHeight: size.height };
};
